import {Component, HostListener, OnDestroy, OnInit, ViewChild, HostBinding, ElementRef} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {DreamDestinationModalComponent, DreamDestinationModalData} from '../components/modals/dream-destination-modal/dream-destination-modal.component';
import {openModal, openModalClose$,} from '../utilities/helpers';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {TopbarNavModalComponent} from '../components/modals/topbar-nav-modal/topbar-nav-modal.component';
import {
    MOBILE_MORE_LINKS,
    MOBILE_MORE_SIGNED_OUT_LINKS,
    MOBILE_TAB_LINKS,
    TOPBAR_NAV_LINKS,
    UPGRADE_QUERY_PARAMS,
} from '../components/modals/topbar-nav-modal/topbar-nav.constants';
import {Destination} from '../models/AirportModel';
import {AirportsService} from '../services/airports.service';
import {AuthService} from '../services/auth.service';
import {UserModel} from '../models/User';
import {catchError, distinctUntilChanged, map, shareReplay, switchMap, take, takeUntil} from 'rxjs/operators';
import {OverlayPanel} from 'primeng/overlaypanel';
import {MOBILE_BREAKPOINT, ViewportService} from '../services/viewport.service';
import {Observable, of, Subject} from 'rxjs';
import {IntercomService} from '../services/intercom.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/internal/operators/filter';
import { MixpanelService } from '../services/mixpanel.service';
import {LoyaltyService} from '../services/loyalty.service';
import {pointsToDollars} from '../utilities/loyalty-points';
import * as Sentry from '@sentry/angular';
import {AnalyticsEvent, NavSurface} from '../models/AnalyticsModels';


declare var window: any;
@Component({
    selector: 'app-topbar',
    templateUrl: './app.topbar.component.html',
    styleUrls: ['./app.topbar.component.css'],
})
export class AppTopbarComponent implements OnInit, OnDestroy {
    @ViewChild('op') overlayPanel!: OverlayPanel;
    @HostBinding('class.has-waitlist-banner')
        get hasWaitlistBanner(): boolean {
        return this.user?.isOnWaitlist ?? false;
    }
    @ViewChild('topbarWrap', { static: true }) topbarWrap!: ElementRef<HTMLElement>;

    openModal = openModal;
    items: MenuItem[] | undefined;
    maxWidth: string = 'none';
    userDestinations: Destination[] = [];
    user: UserModel;
    showMobileDialog = false;
    private destroy$ = new Subject<void>();
    showTopbar = true;
    private ro: ResizeObserver | null = null;
    readonly navLinks = TOPBAR_NAV_LINKS;
    readonly mobileTabs = MOBILE_TAB_LINKS;
    readonly NavSurface = NavSurface;
    readonly availableRewardsDollars$: Observable<number | null>;
    // Held only so a route change can dismiss the sheet — see the NavigationEnd
    // subscription. Scroll and touchmove cannot reach it: the modal puts
    // p-overflow-hidden on the body, so the page does not scroll while it is up.
    private navRef: DynamicDialogRef | null = null;

    socialLinks = [
        {
            url: 'https://www.youtube.com/c/DollarFlightClub',
            icon: 'ph-youtube-logo',
            label: 'youtube'
        },
        {
            url: 'https://x.com/DollarFlights',
            icon: 'ph-x-logo',
            label: 'x'
        },
        {
            url: 'https://www.instagram.com/dollarflightclub/',
            icon: 'ph-instagram-logo',
            label: 'instagram'
        },
        {
            url: 'https://www.facebook.com/DollarFlightClub/',
            icon: 'ph-facebook-logo',
            label: 'facebook'
        }
    ];

    constructor(
        private router: Router,
        public dialogService: DialogService,
        private airportService: AirportsService,
        private auth: AuthService,
        public viewport: ViewportService,
        private intercom: IntercomService,
        private route: ActivatedRoute,
        private mixpanel: MixpanelService,
        private loyalty: LoyaltyService
    ) {
        this.availableRewardsDollars$ = this.auth.user$.pipe(
            map(user => user?.id ?? null),
            distinctUntilChanged(),
            switchMap(userId =>
                userId === null
                    ? of(null)
                    : this.loyalty.getBalance().pipe(
                          map(balance => pointsToDollars(balance.currentPoints)),
                          catchError(error => {
                              Sentry.captureException(error);
                              return of(null);
                          })
                      )
            ),
            shareReplay({bufferSize: 1, refCount: false})
        );
    }
    ngOnInit() {
        this.viewport.resize$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.hideActiveOverlay();
        });
        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe((e: NavigationEnd) => {
                const url = e.urlAfterRedirects || e.url;
                this.showTopbar =
                    !url.includes('/signup') && !url.includes('/onboarding') && !url.includes('/login')
                // Neither dialog closes itself when the route changes beneath
                // it, so browser-back with a sheet open would strand it over
                // the new page.
                this.hideActiveOverlay();
            });
        this.auth.user$
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (u) => {
                    if (u) {
                        this.mixpanel.identify(u.id.toString());
                    } else if (this.user) {
                        this.mixpanel.reset();
                    }
                    this.user = u || null;
                    this.setupMenu();
                },
                error: () => {
                    this.user = null;
                    this.setupMenu();
                }
            });
        void this.auth.loadSession().pipe(take(1)).subscribe();
    }

    ngAfterViewInit(): void {
        this.setTopbarHeightVar();
    
        this.ro = new ResizeObserver(() => this.setTopbarHeightVar());
        this.ro.observe(this.topbarWrap.nativeElement);
      }

      @HostListener('window:load')
      onLoad() {
        this.setTopbarHeightVar();
      }
    
      private setTopbarHeightVar(): void {
        const el = this.topbarWrap?.nativeElement;
        if (!el) return;
    
        const h = el.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--app-topbar-height', `${h}px`);
      }

    @HostListener('window:scroll')
    onWindowScroll() {
        this.hideActiveOverlay();
    }

    @HostListener('window:touchmove')
    onWindowTouchMove() {
        this.hideActiveOverlay();
    }

    ngOnDestroy() {
        this.ro?.disconnect();
        this.ro = null;
        this.destroy$.next();
        this.destroy$.complete();
    }

    private hideActiveOverlay() {
        this.overlayPanel?.hide();
        if (this.showMobileDialog) {
            this.showMobileDialog = false;
        }
        this.navRef?.close();
        this.navRef = null;
    }

    openMoreSheet(): void {
        const barShowsUpgrade = this.viewport.dimensions.width >= MOBILE_BREAKPOINT;
        this.navRef = openModal(this.dialogService, TopbarNavModalComponent, {
            position: 'bottom',
            style: {width: 'calc(100% - 1.5rem)'},
            contentStyle: {padding: '0 1.25rem 1.25rem 1.25rem', scrollbarWidth: 'none'},
            data: {
                links: this.user ? MOBILE_MORE_LINKS : MOBILE_MORE_SIGNED_OUT_LINKS,
                upgradeLink: !this.user || this.user.isPremiumPlus || barShowsUpgrade ? null : this.user.upgradeLink,
            },
        });
    }

    goExternal(url: string, label: string) {
        this.trackTabClick(label);
        window.open(url, '_blank');
    }

    trackTabClick(label: string, surface?: NavSurface): void {
        this.mixpanel.track(`Clicked ${label} Tab`, surface ? {surface, logged_in: !!this.user} : {});
      }

    setupMenu() {
        const menu: any[] = [];

        if (!this.user?.isPremiumPlus) {
            menu.push({ separator: true });
        }

        menu.push({
            items: [
                {
                    label: 'Flights Dashboard',
                    command: () => {
                        this.hideActiveOverlay();
                        this.router.navigate(['/dashboard'])
                        this.trackTabClick('Flights Dashboard');
                    },
                },
                {
                    label: 'Dream Destinations',
                    command: () => {
                        this.hideActiveOverlay();
                        this.openDestinations();
                        this.trackTabClick('Dream Destinations');
                    },
                },
                {
                    label: 'Perks',
                    command: () => {
                        this.hideActiveOverlay();
                        this.router.navigate(['/member-perks']);
                        this.trackTabClick('Perks');
                    },
                },
                {
                    label: 'Flight Settings',
                    command: () => {
                        this.hideActiveOverlay();
                        this.router.navigate(['/account', 'flight-alerts']);
                        this.trackTabClick('Flight Settings');
                    },
                },
                {
                    label: 'Account Settings',
                    command: () => {
                        this.hideActiveOverlay();
                        this.router.navigate(['/account', 'settings']);
                        this.trackTabClick('Account Settings');
                    },
                },
            ],
        });

        menu.push({ separator: true });
        menu.push({
            items: [
                {
                    label: 'Refer Friends',
                    command: () => {
                        {
                        this.hideActiveOverlay();
                        this.router.navigate(['/referral'])                    };
                        this.trackTabClick('Refer Friends');
                    },
                },
                {
                    label: 'Articles',
                    command: () => {
                        this.hideActiveOverlay();
                        window.open('https://dollarflightclub.com/articles', '_blank');
                        this.trackTabClick('Articles');
                    },
                },
                {
                    label: 'Support Center',
                    command: () => {
                        this.hideActiveOverlay();
                        this.intercom.openMessenger();
                        this.trackTabClick('Support Center');
                    },
                },
                {
                    label: 'Request Feature',
                    command: () => {
                        this.hideActiveOverlay();
                        window.open('https://form.typeform.com/to/DO9Q36jn', '_blank');
                        this.trackTabClick('Request Feature');
                    },
                },
                {
                    label: 'Log out',
                    command: () => {
                        this.hideActiveOverlay();
                        this.auth.logout().subscribe();
                    },
                },
            ],
        });

        menu.push({ separator: true });

        this.items = menu;
    }

    openMenu(event: Event): void {
        if (this.viewport.isMobile) {
            this.showMobileDialog = true;
        } else {
            this.overlayPanel.toggle(event);
        }
    }

    openDestinations() {
        this.airportService.getUsersDestinations().subscribe({
            next: (dests) => {
                this.userDestinations = dests;
                const { close$ } = openModalClose$<DreamDestinationModalData>(
                    this.dialogService,
                    DreamDestinationModalComponent,
                    {
                        header: 'Your Dream Destinations',
                        styleClass: 'header-xl dashboard-modal',
                        data: { destinations: dests, maxCount: this.maxDestinationsAllowed, user: this.user },
                    }
                );
                close$.subscribe((updated) => {
                    if (Array.isArray(updated)) this.userDestinations = updated;
                });
            },
            error: (e) => {
                if(e.status === 401){
                    const returnUrl = this.buildReturnUrlWithIntent()
                    this.router.navigate(['/login'], {queryParams: {returnUrl}})
                    return
                }
                console.error('refresh-before-open failed', e);
            },
        });
    }

    get maxDestinationsAllowed():number {
        return this.user.maxDestinationsAllowed
    }

    private buildReturnUrlWithIntent(): string {
        const tree = this.router.createUrlTree([], {
            relativeTo: this.route,
            queryParamsHandling: 'merge'
        });
        return tree.toString();
    }

    goToUpgradeFromMenu(): void {
        this.mixpanel.trackForUser(AnalyticsEvent.ClickedUpgradeCta, {upgrade_source: UPGRADE_QUERY_PARAMS.upgrade_source});
        this.hideActiveOverlay();
        this.router.navigate([this.user.upgradeLink], {queryParams: UPGRADE_QUERY_PARAMS});
      }

}
